// Fuente única de los planes de suscripción (datos + helpers). Antes vivía
// inline en `Checkout.tsx`; se extrajo para compartirla con el alta enterprise
// (`EnterpriseCheckout.tsx`) sin duplicar precios/features. Ver ADR.

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number; // precio en ARS (Argentina); otros países se resuelven aparte
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const plans: SubscriptionPlan[] = [
  {
    id: "emprendimiento",
    name: "Básico",
    price: 21999,
    description:
      "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
    features: [
      "Hasta 60 imágenes por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "~Generación de videos con IA",
    ],
    max_fotos_mes: 60,
  },
  {
    id: "negocio",
    name: "Pro",
    price: 69999,
    description:
      "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
    features: [
      "Hasta 250 imágenes por mes",
      "Hasta 20 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte prioritario",
    ],
    max_fotos_mes: 250,
  },
  {
    id: "enterprise",
    name: "Ultra",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 900 imágenes por mes",
      "Hasta 40 videos por mes",
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 900,
  },
  {
    id: "enterprise1",
    name: "Ultra Nivel 1",
    price: 109999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 500 imágenes por mes",
      "Hasta 40 videos por mes",
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 500,
  },
  {
    id: "enterprise2",
    name: "Ultra Nivel 2",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 1.000 imágenes por mes",
      "Hasta 80 videos por mes",
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 1000,
  },
  {
    id: "enterprise3",
    name: "Ultra Nivel 3",
    price: 399999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 2.000 imágenes por mes",
      "Hasta 160 videos por mes",
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 2000,
  },
];

export function getPlanById(id: string): SubscriptionPlan | undefined {
  return plans.find((p) => p.id === id);
}

/**
 * Slug del link de alta enterprise → `planId`. SOLO planes que tienen `pln_` en
 * Rebill (`REBILL_PLAN_IDS`): el id base `enterprise` NO está, por eso se usan
 * los niveles. Cada plan = su propio link (ej. `/alta-empresa/ultra-1`).
 */
export const ENTERPRISE_PLAN_SLUGS: Record<string, string> = {
  basico: "emprendimiento",
  pro: "negocio",
  "ultra-1": "enterprise1",
  "ultra-2": "enterprise2",
  "ultra-3": "enterprise3",
};

/** Precio formateado en ARS (ej. `$109.999 ARS`). */
export function formatArs(amount: number): string {
  return `$${new Intl.NumberFormat("es-AR").format(amount)} ARS`;
}

// ── Precios y moneda por país ────────────────────────────────────────────
//
// Vivían como funciones locales dentro de `Checkout.tsx`. Se movieron acá para
// que el alta por link (`EnterpriseCheckout.tsx`) use EXACTAMENTE los mismos
// precios en vez de asumir ARS — que era el bug: un usuario de Chile veía
// precios argentinos y el formulario de Rebill. Ver ADR-592.
//
// El cuerpo de estas funciones se movió VERBATIM: mismos valores, mismo orden,
// mismas ramas. Lo único que cambió es que los flags de país (`isArgentina`,
// `isChile`, …) ahora se derivan del parámetro `pais` en vez de venir del
// scope del componente.

export interface PaisFlags {
  isArgentina: boolean;
  isChile: boolean;
  isColombia: boolean;
  isMexico: boolean;
  isPeru: boolean;
}

/** Normaliza el país y devuelve los flags. Misma lógica que tenía Checkout. */
export function paisFlags(pais: string | null | undefined): PaisFlags {
  const normalizedPais = (pais || "").toLowerCase();
  return {
    isArgentina: normalizedPais === "argentina",
    isChile: normalizedPais === "chile",
    isColombia: normalizedPais === "colombia",
    isMexico: normalizedPais === "méxico" || normalizedPais === "mexico",
    isPeru: normalizedPais === "perú" || normalizedPais === "peru",
  };
}

/** Moneda del país. Todo lo no contemplado cae a USD. */
export function currencyForPais(pais: string | null | undefined): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  return isArgentina
    ? "ARS"
    : isChile
      ? "CLP"
      : isColombia
        ? "COP"
        : isMexico
          ? "MXN"
          : isPeru
            ? "PEN"
            : "USD";
}

/** Precio del plan en la moneda del país. `arsPrice` es el precio base (ARS). */
export function getPlanPriceForPais(
  id: string,
  arsPrice: number,
  pais: string | null | undefined,
): number {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  if (isArgentina) return arsPrice;
  if (isChile) {
    switch (id) {
      case "emprendimiento":
        return 14999;
      case "negocio":
        return 49999;
      case "enterprise":
      case "enterprise1":
        return 79999;
      case "enterprise2":
        return 149999;
      case "enterprise3":
        return 299999;
      default:
        return 0;
    }
  }
  if (isColombia) {
    switch (id) {
      case "emprendimiento":
        return 59999;
      case "negocio":
        return 199000;
      case "enterprise":
      case "enterprise1":
        return 299999;
      case "enterprise2":
        return 549999;
      case "enterprise3":
        return 1099999;
      default:
        return 0;
    }
  }
  if (isMexico) {
    switch (id) {
      case "emprendimiento":
        return 299;
      case "negocio":
        return 999;
      case "enterprise":
      case "enterprise1":
        return 1699;
      case "enterprise2":
        return 2999;
      case "enterprise3":
        return 5999;
      default:
        return 0;
    }
  }
  if (isPeru) {
    switch (id) {
      case "emprendimiento":
        return 49;
      case "negocio":
        return 179;
      case "enterprise":
      case "enterprise1":
        return 299;
      case "enterprise2":
        return 549;
      case "enterprise3":
        return 1099;
      default:
        return 0;
    }
  }
  switch (id) {
    case "emprendimiento":
      return 15;
    case "negocio":
      return 50;
    case "enterprise":
    case "enterprise1":
      return 80;
    case "enterprise2":
      return 150;
    case "enterprise3":
      return 300;
    default:
      return 0;
  }
}

/** Precio formateado con la moneda y el locale del país. */
export function formatPriceForPais(
  amount: number,
  pais: string | null | undefined,
): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  const currencyCode = currencyForPais(pais);
  const locale = isArgentina
    ? "es-AR"
    : isChile
      ? "es-CL"
      : isColombia
        ? "es-CO"
        : isMexico
          ? "es-MX"
          : isPeru
            ? "es-PE"
            : "en-US";
  // USD: precios siempre se muestran como ".99" (valor interno entero - 0.01).
  // Resto de monedas: entero sin decimales (mantienen el comportamiento previo).
  const isUSD = currencyCode === "USD";
  const displayAmount = isUSD && amount > 0 ? amount - 0.01 : amount;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: isUSD ? 2 : 0,
    maximumFractionDigits: isUSD ? 2 : 0,
  });
  return `$${fmt.format(displayAmount)} ${currencyCode}`;
}

/**
 * Código ISO → nombre de país, en el MISMO formato que usa `CountrySelect`
 * (`components/OnboardingSelects.tsx`) y que espera `paisFlags`.
 *
 * La landing manda el país detectado como código en la URL del alta
 * (`/alta-empresa/basico?pais=CL`). Acá se traduce para poder resolver el
 * precio en moneda local desde el primer render y pre-seleccionar el país en
 * el formulario. Ver ADR-592.
 *
 * "OTHER" y cualquier código desconocido devuelven `null`: no se inventa un
 * país. Sin país, el precio cae a USD, que es el fallback correcto para todo
 * lo que no tiene tabla propia.
 */
export const PAIS_POR_CODIGO_ISO: Record<string, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  MX: 'México',
  PE: 'Perú',
};

export function paisDesdeCodigoISO(
  codigo: string | null | undefined,
): string | null {
  if (!codigo) return null;
  return PAIS_POR_CODIGO_ISO[codigo.trim().toUpperCase()] ?? null;
}
